import { Modal, Button, Form, FormGroup, ControlLabel, FormControl, SelectPicker, CheckPicker, HelpBlock } from 'rsuite';
import styles from './ModalCreateEditSegments.module.scss';
import { useSetupISACustomSegmentsStore } from '../../SetupISACustomSegments.store';
import { Controller, useForm } from 'react-hook-form';
import { useSaveCustomSegment } from '../../hooks/useSaveCustomSegment';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useChainFilter } from '../../hooks/useChainFilter';
import { LoadingMenu, LoadingSpinerLocal } from '../../../../../../../components';
import { useEffect } from 'react';
import { useEditCustomSegment } from '../../hooks/useEditCustomSegment';
import { CreateCustomSegmentDTO } from '../../types';
import { CONFIRM, SEGMENT_TYPE_LIST, TITLE } from '../../constants';

type ItemType = string;

interface SegmentFormValues {
  segment_name: string;
  items: ItemType[];
}

type ModalEditSegmentsProps = {
  show: boolean;
  cancelDisabled?: boolean;
  confirmDisabled?: boolean;
};

const schema = z.object({
  segment_name: z.string().min(1, 'empty'),
  items: z.array(z.string()).min(1, 'empty'),
});

export const ModalCreateEditSegments = ({ show, cancelDisabled, confirmDisabled }: ModalEditSegmentsProps) => {
  const { control, handleSubmit, setValue, setError } = useForm({
    defaultValues: {
      segment_name: '',
      segment_type: 'Redes',
      items: [] as string[],
    },
    resolver: zodResolver(schema),
  });

  const { segmentActionType, chainFilterData, saveSegmentBody, setCreateEditSegmentCloseModal } = useSetupISACustomSegmentsStore();

  const { handleSaveSegmentMutation, isSaveLoading } = useSaveCustomSegment(setValue, setError);
  const { handleEditSegmentMutation, isEditLoading } = useEditCustomSegment(setValue);
  const { handleSearchChainFilter, handleSelectChain, handleCleanChain } = useChainFilter();

  const handleClose = () => {
    setCreateEditSegmentCloseModal();
    setValue('segment_name', '');
    setValue('items', []);
    setError('segment_name', { message: '' });
  };

  const setData = (data: SegmentFormValues): CreateCustomSegmentDTO => {
    return {
      nomeSegmento: data.segment_name,
      codigoSegmento: '',
      tipoSegmento: 'REDE',
      itens: data.items,
    };
  };

  const submit = (data: SegmentFormValues) => {
    const state = useSetupISACustomSegmentsStore.getState();
    const body = setData(data);

    if (state.segmentActionType === 'create') {
      handleSaveSegmentMutation(body);
    } else {
      handleEditSegmentMutation({ id: state.selectedSegmentID, payload: body });
    }
  };

  useEffect(() => {
    if (segmentActionType === 'edit') {
      setValue('segment_name', saveSegmentBody.nomeSegmento);
      setValue('items', saveSegmentBody.itens);
    }
  }, [segmentActionType]);

  return (
    <Modal backdrop show={show} className={styles['modal-edit-segments']} onHide={handleClose}>
      <LoadingSpinerLocal loading={isSaveLoading || isEditLoading} />
      <Form onSubmit={handleSubmit(submit)}>
        <Modal.Header closeButton className={styles.header} onHide={handleClose}>
          <Modal.Title className={styles.title}>{TITLE[segmentActionType]}</Modal.Title>
          {segmentActionType === 'create' ? <p className={styles.subtitle}>Crie um segmento para utilizar como filtro. </p> : null}
        </Modal.Header>
        <Modal.Body className={styles.body}>
          <div className={styles['form-first-row']}>
            <Controller
              name="segment_name"
              control={control}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <FormGroup className={styles['form-group-input']}>
                  <ControlLabel>Nome do segmento</ControlLabel>
                  <FormControl
                    name="segment_name"
                    className={styles[error?.message ? 'segment-name-input-error' : 'segment-name-input']}
                    value={value}
                    onChange={onChange}
                  />
                  {error?.message && (
                    <HelpBlock className={styles['error-message']} name="segment_name">
                      {error?.message}
                    </HelpBlock>
                  )}
                </FormGroup>
              )}
            />
            <FormGroup className={styles['form-group-selectpicker']}>
              <ControlLabel>Tipo de segmento</ControlLabel>
              <FormControl name="segment_type" disabled accepter={SelectPicker} data={SEGMENT_TYPE_LIST} value="redes" />
            </FormGroup>
          </div>
          <Controller
            name="items"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormGroup className={styles['form-group-checkpicker']}>
                <ControlLabel>Adicionar redes ao segmento</ControlLabel>
                <FormControl
                  name="items"
                  className={styles[error?.message !== 'empty' ? '' : 'segment-name-input-error']}
                  accepter={CheckPicker}
                  data={chainFilterData.list}
                  value={value}
                  onChange={onChange}
                  onOpen={() => handleSearchChainFilter('')}
                  onSelect={(_v, item) => handleSelectChain(item)}
                  onSearch={handleSearchChainFilter}
                  onClean={handleCleanChain}
                  renderMenu={menu => (
                    <span>
                      <LoadingMenu area="segment-chain-filter" />
                      {menu}
                    </span>
                  )}
                />
              </FormGroup>
            )}
          />
        </Modal.Body>
        <Modal.Footer className={styles.footer}>
          <Button className={styles['cancel-btn']} onClick={handleClose} appearance="ghost" color="blue" disabled={cancelDisabled}>
            Cancelar
          </Button>
          <Button className={styles['confirm-btn']} type="submit" appearance="primary" disabled={confirmDisabled} id="modal-edit-segments-confirm-btn">
            {CONFIRM[segmentActionType]}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
